import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * The sticky title block at the top of a scrolling app page (Whispers, Pro,
 * Alerts). It bleeds to the screen edge and sits flush under the app shell's
 * bar. Instead of a hard divider — which reads as a floating card, especially
 * in dark mode — the scrolling content simply melts away underneath through a
 * short gradient fade. Fully opaque, so nothing ghosts through.
 *
 *   <AppPageHeader title="Whispers" action={<Button…/>}>
 *     <Tabs … />
 *   </AppPageHeader>
 */
export function AppPageHeader({
  title,
  eyebrow,
  action,
  children,
  className,
}: {
  title: ReactNode;
  /** Small tracked label above the title (e.g. "Pro"). */
  eyebrow?: ReactNode;
  /** Optional trailing control on the title row (e.g. "Mark all read"). */
  action?: ReactNode;
  /** Rows below the title that should also stay pinned — tabs, a search field. */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'sticky top-0 z-20 -mx-6 -mt-4 flex flex-col gap-3 bg-canvas px-6 pb-3 pt-4 sm:-mx-8 sm:px-8',
        // content dissolves under the header rather than hitting a hairline
        'relative after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-4 after:bg-linear-to-b after:from-canvas after:to-transparent',
        className,
      )}
    >
      <div className="flex min-h-9 items-start justify-between gap-2">
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
              {eyebrow}
            </p>
          )}
          <h1 className="font-display text-2xl font-semibold">{title}</h1>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
